import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import {
  MapOptionType,
  LocationFilterSizeType,
  SelectOptionType,
  MUILocationFilterType,
  LocationLabels,
} from '../utils/Types';
import {
  fetchVillagesOfDistricts,
  locationFilterSelector,
} from '../features/locationFilterSlice';
import { useAppDispatch, useAppSelector } from '../store/redux-hooks';
import MSelect from './MSelect';
import {
  DEFAULT_OPTION_LOCATION_SET_TARGET,
  NO_DATA_OPTION,
} from '../utils/Constant';
import MAutoComplete from './MAutoComplete';

interface ITrackLocationFilterProps {
  filterData: MUILocationFilterType;
  setLocationFilter: (filterData: MUILocationFilterType) => void;
  size?: LocationFilterSizeType;
  disabled?: boolean;
  defaultLocationOptions?: any;
  required?: boolean;
  labels?: LocationLabels;
  dataTestIds?: LocationLabels;
  includeAllOption?: boolean;
  selectFilterOther?: boolean;
  setInputValue?: (term: any) => void;
  searchTerm?: boolean;
  setSearchTerm?: (term: any) => void;
  zoneRequired?: boolean;
  stateRequired?: boolean;
  districtRequired?: boolean;
}

const MUILocationFilter: React.FunctionComponent<ITrackLocationFilterProps> = ({
  filterData,
  setLocationFilter,
  size,
  disabled = false,
  defaultLocationOptions,
  required = false,
  labels,
  dataTestIds,
  includeAllOption,
  selectFilterOther = false,
  setInputValue = () => {},
  searchTerm = false,
  setSearchTerm = () => {},
  zoneRequired = false,
  stateRequired = false,
  districtRequired = false,
}) => {
  const dispatch = useAppDispatch();
  const [stateOption, setStateOption] = useState<SelectOptionType[]>([
    includeAllOption ? DEFAULT_OPTION_LOCATION_SET_TARGET : NO_DATA_OPTION,
  ]);
  const [districtOption, setDistrictOption] = useState<SelectOptionType[]>([
    includeAllOption ? DEFAULT_OPTION_LOCATION_SET_TARGET : NO_DATA_OPTION,
  ]);
  const [selectedSearchTerm, setSelectedSearchterm] = useState<boolean>(false);
  const districtData: MapOptionType = useAppSelector(
    locationFilterSelector
  ).districtData;
  const cityOptions: MapOptionType = useAppSelector(
    locationFilterSelector
  ).cityOptions;
  const allStates: MapOptionType = useAppSelector(
    locationFilterSelector
  ).statesData;
  const zoneOptionRaw: SelectOptionType[] = useAppSelector(
    locationFilterSelector
  ).zoneOptions;
  const zoneOption =
    includeAllOption && zoneOptionRaw && zoneOptionRaw.length > 1
      ? [DEFAULT_OPTION_LOCATION_SET_TARGET, ...zoneOptionRaw]
      : zoneOptionRaw;

  const setStateArrayData = (array: SelectOptionType[]) => {
    if (array && array.length === 1) {
      setStateOption(array);
    } else if (includeAllOption) {
      setStateOption([DEFAULT_OPTION_LOCATION_SET_TARGET, ...(array || [])]);
    } else {
      setStateOption(array || [NO_DATA_OPTION]);
    }
  };
  const onZoneChange = (event: any) => {
    setLocationFilter({
      ...filterData,
      zone: event.target.value,
      state: includeAllOption ? 'All' : '',
      district: includeAllOption ? 'All' : '',
      city: includeAllOption ? 'All' : '',
    });

    if (filterData?.zone != event.target.value) {
      setInputValue('');
    }
    setStateArrayData(
      event?.target?.value != 'All' ? allStates[event.target.value] : []
    );
    setDistrictOption([
      includeAllOption ? DEFAULT_OPTION_LOCATION_SET_TARGET : NO_DATA_OPTION,
    ]);
  };
  const onStateChange = (event: any) => {
    setLocationFilter({
      ...filterData,
      state: event.target.value,
      district: includeAllOption ? 'All' : '',
      city: includeAllOption ? 'All' : '',
    });
    if (filterData?.state != event.target.value) {
      setInputValue('');
    }
    setDistrictOption(
      event?.target?.value
        ? [
            ...(includeAllOption ? [DEFAULT_OPTION_LOCATION_SET_TARGET] : []),
            ...(districtData[event.target.value] || []),
          ]
        : []
    );
  };

  useEffect(() => {
    if (searchTerm) {
      setSelectedSearchterm(true);
    }
  }, [searchTerm]);
  useEffect(() => {
    if (
      !defaultLocationOptions?.zoneOption?.length &&
      zoneOption &&
      zoneOption.length === 1 &&
      !selectedSearchTerm
    ) {
      onZoneChange({ target: { value: zoneOption[0].value } });
    }
  }, [zoneOption, selectedSearchTerm]);
  useEffect(() => {
    if (
      !defaultLocationOptions?.zoneOption?.length &&
      stateOption &&
      stateOption.length === 1 &&
      !selectedSearchTerm
    ) {
      onStateChange({ target: { value: stateOption[0].value } });
    }
  }, [stateOption, selectedSearchTerm]);

  useEffect(() => {
    setStateArrayData(
      filterData?.zone && filterData.zone != 'All'
        ? allStates[filterData.zone]
        : []
    );
  }, [filterData?.zone]);

  useEffect(() => {
    if (selectFilterOther && filterData?.state) {
      const districts = districtData[filterData?.state] || [];
      setDistrictOption([
        ...(includeAllOption ? [DEFAULT_OPTION_LOCATION_SET_TARGET] : []),
        ...districts,
      ]);
    }
  }, [districtData, filterData?.state]);

  useEffect(() => {
    if (searchTerm) {
      if (
        filterData?.state &&
        filterData.state !== 'All' &&
        districtData[filterData?.state]
      ) {
        const districts = districtData[filterData.state];
        setDistrictOption([
          ...(includeAllOption ? [DEFAULT_OPTION_LOCATION_SET_TARGET] : []),
          ...districts,
        ]);
        if (
          filterData?.district &&
          filterData.district !== 'All' &&
          !cityOptions[filterData.district]
        ) {
          dispatch(
            fetchVillagesOfDistricts({
              districtId: [Number(filterData.district)],
            })
          );
        }
        setSearchTerm(false);
      } else {
        setDistrictOption([
          includeAllOption
            ? DEFAULT_OPTION_LOCATION_SET_TARGET
            : NO_DATA_OPTION,
        ]);
        setSearchTerm(false);
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    if (defaultLocationOptions?.cityOption?.length) {
      dispatch(
        fetchVillagesOfDistricts({
          districtId: [Number(filterData?.district)],
        })
      );
    }
  }, [defaultLocationOptions]);

  const villageOptions =
    defaultLocationOptions?.cityOption ||
    (filterData.district
      ? [
          ...(includeAllOption ? [DEFAULT_OPTION_LOCATION_SET_TARGET] : []),
          ...(cityOptions?.[filterData.district] ?? []),
        ]
      : [NO_DATA_OPTION]);

  return (
    <Row>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 2} className="mb-3">
        <MSelect
          label={labels?.zone ?? 'Zone'}
          options={
            defaultLocationOptions?.zoneOption ?? zoneOption ?? [NO_DATA_OPTION]
          }
          value={filterData.zone}
          required={zoneRequired ? zoneRequired : required}
          onChange={(event: any) => {
            onZoneChange(event);
          }}
          disabled={disabled}
          dataTestId={dataTestIds?.zone}
        />
      </Col>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 4} className="mb-3">
        <MSelect
          label={labels?.state ?? 'State'}
          required={stateRequired ? stateRequired : required}
          options={
            defaultLocationOptions?.stateOption ??
            (filterData.zone
              ? stateOption ?? [NO_DATA_OPTION]
              : [NO_DATA_OPTION])
          }
          value={filterData.state ?? ''}
          onChange={(event: any) => {
            onStateChange(event);
          }}
          disabled={disabled}
          dataTestId={dataTestIds?.state}
        />
      </Col>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 2} className="mb-2">
        <MSelect
          label={labels?.district ?? 'District'}
          required={districtRequired ? districtRequired : required}
          options={
            defaultLocationOptions?.districtOption ??
            (filterData.state
              ? districtOption ?? [NO_DATA_OPTION]
              : [NO_DATA_OPTION])
          }
          value={filterData.district ?? ''}
          onChange={(event: any) => {
            setLocationFilter({
              ...filterData,
              district: event.target.value,
              city: includeAllOption ? 'All' : '',
            });

            if (filterData?.district != event.target.value) {
              setInputValue('');
            }

            if (event?.target?.value != 'All') {
              if (!cityOptions[event.target.value]) {
                dispatch(
                  fetchVillagesOfDistricts({
                    districtId: [Number(event.target.value)],
                  })
                );
              }
            }
          }}
          disabled={disabled}
          dataTestId={dataTestIds?.district}
        />
      </Col>
      <Col xs={size?.zone?.xs ?? 6} md={size?.zone?.md ?? 4} className="mb-2">
        <MAutoComplete
          label={labels?.city ?? 'Village'}
          options={villageOptions}
          value={(() => {
            const district = filterData?.district;
            if (!district) {
              return { value: '', label: '' };
            }

            const currentCity = filterData?.city ?? '';
            console.log('currentCity', currentCity);
            const match =
              cityOptions?.[district]?.find(
                (opt: any) => opt.value == currentCity
              ) ?? null;
            return currentCity == 'All'
              ? { value: 'All', label: 'All', pincode: '' }
              : match
                ? {
                    value: match.value,
                    label: match.label,
                    pincode: match.pincode,
                  }
                : { value: '', label: '', pincode: '' };
          })()}
          onChange={(value: any) => {
            const district = filterData?.district;
            const selectedVillage =
              district != null
                ? cityOptions?.[district]?.find(
                    (item: any) => item.value === value?.value
                  )
                : undefined;

            setLocationFilter({
              ...filterData,
              city: value?.value,
              pincode: selectedVillage ? selectedVillage?.pincode : '',
            });

            setInputValue(selectedVillage?.pincode ?? '');
          }}
          disabled={disabled}
          dataTestId={dataTestIds?.city}
          minCharsToFilter={true}
          required={required}
        />
      </Col>
    </Row>
  );
};

export default MUILocationFilter;
